import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Users, Plus, Edit, Trash2, Save, ArrowLeft, Loader2 } from "lucide-react";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterPrimaryButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterEditButton, 
  masterDeleteButton, 
  masterFormContainer, 
  masterFormSectionHeader, 
  masterFormGrid2, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect, 
  masterFormActionBlock, 
  masterActionButtonsContainer,
  masterReadOnly,
  masterTableDatastatus
} from '../../GlobalManagement/Pages/masterPageStyles';

const DepartmentHead = () => {
  const [departmentHeadId, setDepartmentHeadId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [savedHeads, setSavedHeads] = useState([]);

  const [showForm, setShowForm] = useState(false); // Single-page toggle view
  const [editingHeadId, setEditingHeadId] = useState("");
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedDepartmentCode, setSelectedDepartmentCode] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const selectedEmployee = useMemo(
    () => employees.find((e) => e._id === selectedEmployeeId) || null,
    [employees, selectedEmployeeId]
  );

  const selectedDepartmentName = useMemo(() => {
    const dep = departments.find((d) => d.deptCode === selectedDepartmentCode);
    return dep?.deptName || "";
  }, [departments, selectedDepartmentCode]);

  const filteredEmployees = useMemo(() => {
    const q = searchTerm.trim().toUpperCase();
    if (!q) return employees;
    return employees.filter((e) => {
      const fullName = `${e.firstName || ""} ${e.middleName || ""} ${e.lastName || ""}`
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
      return (
        fullName.includes(q) ||
        String(e.employeeID || "").toUpperCase().includes(q) ||
        String(e.employeeUserId || "").toUpperCase().includes(q)
      );
    });
  }, [employees, searchTerm]);

  const autoDesignationRows = useMemo(
    () =>
      designations
        .filter((d) => d.departmentName === selectedDepartmentName)
        .map((d) => ({
          designationID: d.designationID,
          designationName: d.designationName,
        }))
        .filter((d) => d.designationID && d.designationName),
    [designations, selectedDepartmentName]
  );

  const autoDesignationData = autoDesignationRows.map((d) => ({
    id: d.designationID,
    name: d.designationName,
  }));

  const formatSearchInput = (value) => {
    let formatted = value.toUpperCase().replace(/\s+/g, " ");
    const compact = formatted.replace(/\s+/g, "");
    if (!compact.includes("-")) {
      const match = compact.match(/^([A-Z]+)(\d.*)$/);
      if (match) {
        return `${match[1]}-${match[2]}`;
      }
    }
    return formatted;
  };

  const loadNextId = async () => {
    const token = localStorage.getItem("token");
    const idRes = await axios.get("/api/department-heads/next-id", {
      headers: { Authorization: `Bearer ${token}` }
    });
    setDepartmentHeadId(idRes?.data?.departmentHeadId || "");
  };

  const loadSavedHeads = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get("/api/department-heads", {
      headers: { Authorization: `Bearer ${token}` }
    });
    setSavedHeads(Array.isArray(res.data) ? res.data : []);
  };

  const resetForm = async () => {
    setEditingHeadId("");
    setSelectedEmployeeId("");
    setSelectedDepartmentCode("");
    setSearchTerm("");
    await loadNextId();
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const [empRes, deptRes, desigRes] = await Promise.all([
          axios.get("/api/employees", config),
          axios.get("/api/departments", config),
          axios.get("/api/designations", config),
        ]);
        setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
        setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
        setDesignations(Array.isArray(desigRes.data) ? desigRes.data : []);
        await Promise.all([loadNextId(), loadSavedHeads()]);
      } catch (err) {
        toast.error("Failed to load data");
      }
    };
    loadData();
  }, []);

  const handleAddNew = async () => {
    await resetForm();
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) {
      toast.error("Please select employee");
      return;
    }
    if (!selectedDepartmentCode || !selectedDepartmentName) {
      toast.error("Please select department");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const fullName = `${selectedEmployee.firstName || ""} ${selectedEmployee.middleName || ""} ${
        selectedEmployee.lastName || ""
      }`
        .replace(/\s+/g, " ")
        .trim();

      const payload = {
        departmentHeadId,
        employeeUserId: selectedEmployee.employeeUserId || "",
        employeeID: selectedEmployee.employeeID || "",
        employeeName: fullName,
        departmentHeadName: fullName,
        departmentID: selectedDepartmentCode,
        departmentName: selectedDepartmentName,
        designationData: autoDesignationData,
      };

      if (editingHeadId) {
        await axios.put(`/api/department-heads/${editingHeadId}`, payload, config);
        toast.success("Department head updated successfully");
      } else {
        await axios.post("/api/department-heads", payload, config);
        toast.success("Department head saved successfully");
      }

      await loadSavedHeads();
      setShowForm(false);
      await resetForm();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    const emp = employees.find(
      (e) => e.employeeUserId === row.employeeUserId || e.employeeID === row.employeeID
    );
    setEditingHeadId(row._id);
    setDepartmentHeadId(row.departmentHeadId || "");
    setSelectedEmployeeId(emp?._id || "");
    setSelectedDepartmentCode(row.departmentID || "");
    setSearchTerm("");
    setShowForm(true);
  };

  const handleDelete = async (rowId) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/department-heads/${rowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted successfully");
      await loadSavedHeads();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete");
    }
  };

  const filteredData = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return savedHeads;
    return savedHeads.filter((row) => {
      const headName = (row.departmentHeadName || row.employeeName || "").toLowerCase();
      const headId = (row.departmentHeadId || "").toLowerCase();
      const deptName = (row.departmentName || "").toLowerCase();
      return headName.includes(q) || headId.includes(q) || deptName.includes(q);
    });
  }, [savedHeads, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  return (
    <div className={masterBodyArea}>
      <Sidebar />
       <div className={`${masterPageShell} ${masterContentArea}`}>
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Users size={14} /></div>
              <div><h1 className={masterTitle}>Department Head Master</h1></div>
            </div>
            <button 
              type="button"
              onClick={showForm ? () => setShowForm(false) : handleAddNew} 
              className={`${showForm ? masterPrimaryButton : masterAccentButton} shrink-0 inline-flex items-center gap-1`}
            >
              {showForm ? <><ArrowLeft size={10} /> VIEW LIST</> : <><Plus size={10} /> ADD DEPARTMENT HEAD</>}
            </button>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full`}>
          {!showForm ? (
            <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
              {/* Search & Pagination Toolbar */}
              <div className="border-b border-slate-200 flex-shrink-0 bg-white p-1 sm:p-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by head name, ID or department..." 
                  totalItems={filteredData.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />
              </div>

              {/* Table Container */}
              <div className="overflow-x-auto w-full">
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData} >S.No</th>
                     
                      <th className={masterTableData}>Employee UserID</th>
                      <th className={masterTableData}>Employee ID</th>
                      <th className={masterTableData} >Employee Name</th>
                      <th className={masterTableData}>Dept ID</th>
                      <th className={masterTableData}>Department Name</th>
                      <th className={masterTableData}>Designations</th>
                      <th className={masterTableDatastatus}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row, index) => (
                        <tr key={row._id} className={masterTableRow}>
                          <td className={masterTableCell}>
                            {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                          </td>
                         
                          <td className={masterTableCell}>{row.employeeUserId}</td>
                          <td className={masterTableCell}>{row.employeeID}</td>
                          <td className={masterTableCell} style={{ textAlign: 'left', fontWeight: '600' }}>
                            {row.departmentHeadName || row.employeeName}
                          </td>
                          <td className={masterTableCell}>{row.departmentID || "-"}</td>
                          <td className={masterTableCell} style={{ textAlign: 'left' }}>{row.departmentName}</td>
                          <td className={masterTableCell} style={{ textAlign: 'left' }}>
                            {(row.designationData || []).length > 0
                              ? row.designationData.map((d, idx) => (
                                  <div key={`${d?.id || "id"}-${idx}`}>{idx + 1}. {d?.name || "-"} ({d?.id || "-"})</div>
                                ))
                              : "-"}
                          </td>
                          <td className={masterTableCell}>
                            <div className={masterActionButtonsContainer}>
                              <button 
                                type="button" 
                                onClick={() => handleEdit(row)} 
                                className={masterEditButton}
                                title="Edit"
                              >
                                <Edit size={10} />
                              </button>
                              <button 
                                type="button" 
                                onClick={() => handleDelete(row._id)} 
                                className={masterDeleteButton}
                                title="Delete"
                              >
                                <Trash2 size={10} /> 
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No saved records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Add / Edit Form View */
            <form onSubmit={handleSave} className={masterFormContainer}>
              <div className={masterFormSectionHeader}>
                <h2>{editingHeadId ? 'Edit Department Head Details' : 'Create New Department Head'}</h2>
              </div>

              <div className={masterFormGrid2}>
                

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Department <span className="text-rose-500">*</span></label>
                  <select
                    value={selectedDepartmentCode}
                    onChange={(e) => setSelectedDepartmentCode(e.target.value)}
                    className={masterSelect}
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((d) => (
                      <option key={d._id} value={d.deptCode}>
                        {d.deptName} ({d.deptCode})
                      </option>
                    ))}
                  </select>
                </div>

                <div className={`${masterFormGridRow} md:col-span-2`}>
                  <label className={masterInputLabel}>Designation (Auto by Department)</label>
                  <div className="w-full p-1.5 border border-slate-300 rounded text-xs bg-slate-50 min-h-[30px] font-semibold text-slate-700">
                    {autoDesignationRows.length > 0 ? (
                      autoDesignationRows.map((d, idx) => (
                        <div key={`${d.designationID}-${idx}`}>
                          {idx + 1}. {d.designationName} ({d.designationID})
                        </div>
                      ))
                    ) : (
                      <span className="text-slate-400">No designation found for selected department</span>
                    )}
                  </div>
                </div>

                <div className={`${masterFormGridRow} md:col-span-2`}>
                  <label className={masterInputLabel}>
                    Search Employee (Name / UserID / EmployeeID) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(formatSearchInput(e.target.value))}
                    placeholder="Type name or ID to filter table..."
                    className={masterInput}
                  />
                </div>
              </div>

              {/* Employee Selection Table inside Form */}
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase text-slate-700 border-b border-slate-200">
                  Select Employee Record *
                </div>
                <div className="max-h-60 overflow-y-auto">
                  <table className={masterTable}>
                    <thead>
                      <tr className={masterStickyHeadRow}>
                        <th className={masterTableData} style={{ width: '50px' }}>Select</th>
                        <th className={masterTableData}>Employee ID</th>
                        <th className={masterTableData}>Employee UserID</th>
                        <th className={masterTableData} style={{ textAlign: 'left' }}>Employee Name</th>
                      </tr>
                    </thead>
                    <tbody className={masterTbody}>
                      {filteredEmployees.length > 0 ? (
                        filteredEmployees.map((e) => {
                          const fullName = `${e.firstName || ""} ${e.middleName || ""} ${e.lastName || ""}`
                            .replace(/\s+/g, " ")
                            .trim();
                          return (
                            <tr key={e._id} className={masterTableRow}>
                              <td className={masterTableCell}>
                                <input
                                  type="radio"
                                  name="department-head-employee"
                                  checked={selectedEmployeeId === e._id}
                                  onChange={() => setSelectedEmployeeId(e._id)}
                                  className="accent-[#00b4d8] cursor-pointer"
                                />
                              </td>
                              <td className={masterTableCell}>{e.employeeID}</td>
                              <td className={masterTableCell}>{e.employeeUserId}</td>
                              <td className={masterTableCell} style={{ textAlign: 'left', fontWeight: '600' }}>
                                {fullName}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-slate-300 font-bold uppercase text-xs">
                            No employees found matching filter
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={masterFormActionBlock}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1 rounded border border-slate-300 text-slate-700 font-semibold text-[11px] bg-white hover:bg-slate-50 transition-all uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={masterAccentButton}
                >
                  {saving ? (
                    <Loader2 className="animate-spin" size={10} />
                  ) : (
                    <Save size={10} />
                  )}
                  {editingHeadId ? 'UPDATE DEPARTMENT HEAD' : 'SAVE DEPARTMENT HEAD'}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default DepartmentHead;